import React from 'react';
import { Table, Tag, Image, Tooltip } from 'antd';
import { CircleCheck, CircleX, Eye, Camera, Store } from 'lucide-react';
import dayjs from 'dayjs';
import { formatCurrency } from '../../../../utils/format';
import { PriceCell, StatusTag, IconAction, EmptyState, CollapsibleCard } from '../../../common';
import { useContractStoreResults } from './contractDetail.controller';

function ContractStoreResults({ contract, contractChecks, navigateStore, onStorePriceChange, canEditPrices = true }) {
    const { storeData, totalInvestment, savingStoreId, handlePriceChange } = useContractStoreResults({ contract, contractChecks, onStorePriceChange });

    const columns = [
        {
            title: 'Piso de Venta',
            dataIndex: 'storeName',
            key: 'storeName',
            fixed: 'left',
            width: 220,
            render: (text, record) => (
                <div className="flex items-center gap-3 py-0.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-100 text-surface-500 text-sm">
                        <Store />
                    </span>
                    <div className="min-w-0">
                        <div className="text-sm font-medium text-surface-900 truncate">{text}</div>
                        <div className="text-xs text-surface-500 truncate">{record.clientName}</div>
                    </div>
                </div>
            ),
            sorter: (a, b) => a.storeName.localeCompare(b.storeName)
        },
        {
            title: 'Tipo',
            dataIndex: 'storeType',
            key: 'storeType',
            width: 120,
            render: (text) => (
                <Tag bordered={false} className="!m-0">
                    {text}
                </Tag>
            )
        },
        {
            title: 'Estado de Visita',
            key: 'visitStatus',
            width: 150,
            render: (_, record) => {
                if (!record.visited) {
                    return <StatusTag tone="neutral">No Visitado</StatusTag>;
                }
                return (
                    <StatusTag tone="info" dot>
                        <span className="tabular-nums">{dayjs(record.checkDate).format('DD/MM/YY')}</span>
                    </StatusTag>
                );
            },
            filters: [
                { text: 'Visitado', value: true },
                { text: 'No Visitado', value: false }
            ],
            onFilter: (value, record) => record.visited === value
        },
        {
            title: 'Precio (USD)',
            key: 'price',
            width: 160,
            align: 'right',
            render: (_, record) =>
                canEditPrices ? (
                    <PriceCell
                        value={record.price}
                        isCustom={record.hasCustomPrice}
                        disabled={savingStoreId === record.storeId}
                        tooltip={record.hasCustomPrice ? 'Precio ajustado para este piso' : 'Precio general de la contratación'}
                        onSave={(price) => handlePriceChange(record.storeId, price)}
                    />
                ) : (
                    <span className="tabular-nums font-semibold text-surface-900">{formatCurrency(record.price)}</span>
                ),
            sorter: (a, b) => (a.price || 0) - (b.price || 0)
        },
        {
            title: 'Disponibilidad',
            key: 'availability',
            width: 150,
            render: (_, record) => {
                if (!record.visited) {
                    return <span className="text-surface-400">-</span>;
                }
                return record.available ? (
                    <StatusTag tone="success" icon={<CircleCheck />}>
                        Disponible
                    </StatusTag>
                ) : (
                    <StatusTag tone="danger" icon={<CircleX />}>
                        No Disponible
                    </StatusTag>
                );
            },
            filters: [
                { text: 'Disponible', value: true },
                { text: 'No Disponible', value: false },
                { text: 'Sin Datos', value: null }
            ],
            onFilter: (value, record) => {
                if (value === null) return !record.visited;
                return record.available === value;
            }
        },
        {
            title: 'Evidencia',
            key: 'images',
            width: 220,
            render: (_, record) => {
                if (!record.visited || record.imageCount === 0) {
                    return <span className="text-sm text-surface-400">{!record.visited ? 'No visitado' : 'Sin imágenes'}</span>;
                }

                return (
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-surface-600 tabular-nums">
                            <Camera className="text-surface-400" />
                            {record.imageCount} imagen(es)
                        </span>
                        <Image.PreviewGroup>
                            <div className="flex gap-1">
                                {record.images.slice(0, 2).map((img, idx) => (
                                    <Tooltip key={idx} title="Ver imagen">
                                        <Image
                                            src={img.url}
                                            alt={`Evidence ${idx + 1}`}
                                            width={32}
                                            height={32}
                                            className="object-cover rounded-md border border-surface-200"
                                            preview={{ mask: <Eye className="text-xs" /> }}
                                        />
                                    </Tooltip>
                                ))}
                                {record.imageCount > 2 && (
                                    <div className="w-8 h-8 rounded-md border border-surface-200 flex items-center justify-center bg-surface-50 text-surface-500 text-xs tabular-nums">
                                        +{record.imageCount - 2}
                                    </div>
                                )}
                            </div>
                        </Image.PreviewGroup>
                    </div>
                );
            }
        },
        {
            title: 'Acciones',
            key: 'actions',
            fixed: 'right',
            width: 90,
            align: 'right',
            render: (_, record) => <IconAction icon={<Eye />} label="Ver Piso" onClick={() => navigateStore(record.storeId)} />
        }
    ];

    return (
        <CollapsibleCard
            title="Resultados por Piso de Venta"
            icon={<Store />}
            iconTone="violet"
            defaultExpanded={false}
            flush
            badges={
                <>
                    <StatusTag tone="neutral">{storeData.length} pisos</StatusTag>
                    <StatusTag tone="success">
                        <span className="tabular-nums">{formatCurrency(totalInvestment)}</span>
                    </StatusTag>
                </>
            }>
            {storeData.length === 0 ? (
                <EmptyState icon={<Store />} title="Sin pisos de venta" description="No hay pisos de venta asignados a esta contratación" />
            ) : (
                <>
                    <Table
                        columns={columns}
                        dataSource={storeData}
                        size="middle"
                        pagination={{
                            pageSize: 20,
                            showSizeChanger: true,
                            showTotal: (total) => `Total ${total} pisos`
                        }}
                        scroll={{ x: 1200 }}
                    />
                    <div className="flex items-center justify-between gap-4 px-5 py-4 border-t border-surface-100 bg-surface-25 rounded-b-xl">
                        <span className="text-sm text-surface-600">Inversión total de la contratación ({storeData.length} pisos)</span>
                        <span className="text-lg font-semibold text-surface-900 tabular-nums">{formatCurrency(totalInvestment)}</span>
                    </div>
                </>
            )}
        </CollapsibleCard>
    );
}

export default ContractStoreResults;
